import { environmentById } from '../config/environments';
import { vehicleById } from '../config/vehicles';
import type { Board } from '../net/leaderboard';
import { nameProblem } from '../net/names';
import { t } from './i18n';

function el<T extends HTMLElement = HTMLElement>(selector: string): T {
  const found = document.querySelector<T>(selector);
  if (!found) throw new Error(`Missing element ${selector}`);
  return found;
}

const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** The leaderboard overlay: join form (no name yet), top 10 for the environment, Race #1. */
export class BoardScreen {
  private readonly overlay = el('#board-overlay');
  private readonly list = el('#board-list');
  private readonly status = el('#board-status');
  private readonly join = el<HTMLFormElement>('#board-join');
  private readonly nameInput = el<HTMLInputElement>('#board-name');
  private readonly error = el('#board-error');
  private readonly race = el<HTMLButtonElement>('#board-race');
  onJoin: (name: string) => Promise<void> = async () => {};
  onRace: () => void = () => {};
  onClose: () => void = () => {};

  constructor() {
    this.join.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = this.nameInput.value;
      if (nameProblem(name)) return this.showError(t('board.bad-name'));
      this.onJoin(name).catch(() => this.showError(t('board.bad-name')));
    });
    this.race.addEventListener('click', () => this.onRace());
    el('#board-close').addEventListener('click', () => this.onClose());
  }

  get visible(): boolean {
    return !this.overlay.hidden;
  }

  show(envId: string, hasName: boolean): void {
    el('#board-env').textContent = environmentById(envId)?.name ?? envId;
    this.join.hidden = hasName;
    this.error.hidden = true;
    this.list.replaceChildren();
    this.status.textContent = t('board.loading');
    this.race.hidden = true;
    this.overlay.hidden = false;
    (hasName ? el('#board-close') : this.nameInput).focus();
  }

  hide(): void {
    this.overlay.hidden = true;
  }

  joined(): void {
    this.join.hidden = true;
  }

  render(board: Board): void {
    this.list.innerHTML = board.top
      .map((r) => `<li class="${r.mine ? 'is-mine' : ''}"><span class="rank">#${r.rank}</span><span>${escape(r.name)} <small>#${escape(r.tag)}${r.mine ? ` · ${t('board.you')}` : ''} · ${escape(vehicleById(r.vehicle)?.name ?? r.vehicle)}</small></span><b>${Math.floor(r.distance)} m</b></li>`)
      .join('');
    const me = board.me && !board.top.some((r) => r.mine) ? `${t('board.rank', { rank: board.me.rank, total: board.total })} · ${Math.floor(board.me.best)} m` : '';
    this.status.textContent = board.top.length === 0 ? t('board.empty') : me;
    this.race.hidden = !board.top[0]?.ghost;
  }

  offline(): void {
    this.status.textContent = t('board.offline');
  }

  private showError(text: string): void {
    this.error.textContent = text;
    this.error.hidden = false;
  }
}
